import { useMemo } from 'react';
import ErrorBoundary from '@v2/shared/components/ErrorBoundary';
import ApplePay from '@v2/shared/components/Payment/ApplePay';
import GooglePay from '@v2/shared/components/Payment/GooglePay';
import PayPal from '@v2/shared/components/Payment/PayPal';
import useWalletReveal from '@v2/shared/components/Payment/useWalletReveal';
import { PaymentCart, WalletShape } from '@v2/shared/components/Payment/types';
import { Cart, PaymentConfig } from '@v2/shared/lib/types';

/**
 * The corner every way out of this cart is drawn with — Checkout and the three wallets —
 * and the reason it is named here rather than four times.
 *
 * Four buttons stacked one under another are read as one control, so they have to agree
 * about their corners, and they had drifted into four different ones: an 8px Checkout, a
 * fully-round Apple Pay, a fully-round Google Pay and a 4px PayPal. `rounded` is the
 * legacy cart's corner and the one the drawer settles on — see `WalletShape` for the units
 * each sdk wants it in.
 */
export const CART_WALLET_SHAPE: WalletShape = 'rounded';

type Props = {
  payment?: PaymentConfig | null;
  /** The drawer's cart, as it came back from the api — lines and totals and all. */
  cart?: Cart;
  /**
   * Called once a wallet has authorized a payment, with the payload to hand on.
   *
   * The drawer decides what happens next rather than this component, because what
   * happens next is the same for a wallet as for the Checkout button: the cart may be
   * offered an upgrade first, and only then does anything post. See `beginCheckout`.
   */
  onAuthorized: (paymentPayload: string) => Promise<void>;
};

/**
 * Apple Pay, Google Pay and PayPal for the whole cart, under the Checkout button.
 *
 * The drawer's adapter to the three shared wallet components, and the sibling of the
 * product page's `WalletButtons`. Both turn "what is being bought" into the shape the
 * payment sheets price; the difference is only that this one is handed a cart that
 * already exists instead of assembling a single line from the selected format.
 *
 * ## Why this does not go through the cart api
 *
 * The obvious move — call `addItem(sku, offerCode, payload)` the way the product page
 * does — is the one thing that cannot work here. `AddCartItemAction` treats a payload as
 * "this is the whole order" and **empties the cart** before adding the sku, which is
 * correct for a product page buying one magazine in one tap and catastrophic for a
 * drawer holding nine. There is no add-nothing-just-take-the-payload endpoint, and per
 * Carlos's call on 2026-09-02 we are not building one.
 *
 * So the payload goes where the legacy sale page has always sent it: `cart.php`. The
 * legacy buttons render a hidden `xPaymentPayload` field inside the sidebar's
 * `#addtocart-form` and submit it, which is a POST to `xCmd=update` — a request whose
 * whole job is to re-save a cart being posted back, and there is no such form here.
 *
 * Where exactly it is posted is the drawer's decision, not this component's, and is
 * handed in as `onAuthorized`. An authorized wallet and a pressed Checkout button are the
 * same event as far as the cart is concerned — both may be interrupted by the lightbox
 * deal first — so both go through one path. See `beginCheckout` in `CartDrawer`.
 *
 * ## Why the buttons are not re-rendered when the cart changes
 *
 * The legacy page replaces the whole sidebar's html on every change and lets jQuery
 * re-initialize whatever it finds, so each add or remove tears down all three buttons and
 * runs `braintree.client.create`, a data collector, `isReadyToPay` and
 * `paypal.Buttons().render()` again from nothing. Here the wallets are mounted once and
 * handed a new `cart` prop; each reads the current order through a ref at the moment the
 * sheet opens, so the price follows the drawer without anything being rebuilt. The
 * Braintree client is shared and memoized per page on top of that
 * (`createBraintreeSession`), so even a remount would not re-open the gateway.
 *
 * Closing the drawer does not rebuild them either, and that took a second change: the
 * dialog this sits in is asked to hide its panel rather than tear it down once it has
 * been opened, so a close is a `display: none` and the three sdks are never asked a
 * second time. See `hasOpened` in `CartDrawer` and `keepMounted` in `FullScreenDialog`.
 * The one thing that does still rebuild them is emptying the cart, because the whole
 * footer goes with the last line — and there is nothing to pay for in between.
 *
 * Each wallet sits in its own silent error boundary, as on the product page: a crashed
 * wallet draws nothing and leaves Checkout and the other wallets working, and a crashed
 * Apple Pay opens the others' gate as if it had decided "no".
 */
export default function CartWalletButtons({ payment, cart, onAuthorized }: Props) {
  const checkoutUrl = cart?.checkout_url;

  const paymentCart: PaymentCart = useMemo(() => {
    const items = cart?.items ?? [];

    return {
      // `price` is the line total and travels with its own `quantity`, which is the pair
      // the shipping and tax apis already take from a cart — see
      // TaxCalculator::buildItemRequest. Passing the cart's own numbers through unchanged
      // is what keeps the sheet's total and the order's total the same number.
      items: items.map((item) => ({
        product: { id: item.product.id },
        sku: { code: item.sku.code },
        quantity: item.quantity,
        price: item.price,
        discount: item.discount,
      })),
      total_items_price: cart?.total_items_price ?? 0,
      total_discounts: cart?.total_discounts ?? 0,
      // Both start at zero and are filled in by the sheet once an address is given — the
      // cart cannot know either until it has one.
      shipping_total_amount: 0,
      tax_total_amount: 0,
    };
  }, [cart]);

  /**
   * What PayPal authorizes. It does not reprice against an address, so it is asked for
   * the cart as it stands, which is the same number the drawer prints as the subtotal
   * plus whatever shipping the cart already knows about.
   */
  const paypalAmount = useMemo(
    () =>
      (cart?.total_items_price ?? 0) - (cart?.total_discounts ?? 0) + (cart?.shipping_total_amount ?? 0),
    [cart]
  );

  // Apple Pay shows the moment it is ready; the other two wait until it has finished
  // deciding, so they cannot appear above a button that is about to be drawn over them.
  const reveal = useWalletReveal();

  /*
   * No configuration, no cart, or a cart that came from `#dm-v2-cart` rather than the api
   * — the summary payload carries neither lines nor `checkout_url`, so there would be
   * nothing to price and nowhere to post. The drawer refreshes on open, so this is the
   * state before that lands.
   */
  if (!payment || !checkoutUrl || !paymentCart.items.length) {
    return null;
  }

  return (
    <div className="grid gap-2 mt-2">
      <ErrorBoundary name="Payment.ApplePay" fallback={null} onError={reveal.primary.reportSettled}>
        <ApplePay
          payment={payment}
          cart={paymentCart}
          submitPayload={onAuthorized}
          reveal={reveal.primary}
          shape={CART_WALLET_SHAPE}
        />
      </ErrorBoundary>
      {payment.googleMerchantId && (
        <ErrorBoundary name="Payment.GooglePay" fallback={null}>
          <GooglePay
            payment={payment}
            cart={paymentCart}
            submitPayload={onAuthorized}
            reveal={reveal.secondary}
            shape={CART_WALLET_SHAPE}
          />
        </ErrorBoundary>
      )}
      {payment.paypal && (
        <ErrorBoundary name="Payment.PayPal" fallback={null}>
          <PayPal
            payment={payment}
            amount={paypalAmount}
            submitPayload={onAuthorized}
            reveal={reveal.secondary}
            shape={CART_WALLET_SHAPE}
          />
        </ErrorBoundary>
      )}
    </div>
  );
}
